import dayjs, { Dayjs } from 'dayjs';
import i18next from 'i18next';

import { ChatMessageDto, UserShortDto } from '@/api/generated';
import { capitalizeFirstLetter } from '@/utils/capitalizeFirstLetter.util.ts';

const RUN_GAP_MINUTES = 5;

export type ChatMessageRun = {
  isOwn: boolean;
  messages: ChatTimelineMessage[];
  sender: UserShortDto;
};

export type ChatTimelineEntry =
  | { date: string; key: string; type: 'day' }
  | { key: string; run: ChatMessageRun; type: 'run' }
  | { key: string; type: 'unread' };

export type ChatTimelineMessage = ChatMessageDto & { isPending?: boolean };

export const buildChatTimeline = (params: {
  currentUserId: string;
  firstUnreadMessageId?: number;
  messages: ChatTimelineMessage[];
}): ChatTimelineEntry[] => {
  const { currentUserId, firstUnreadMessageId, messages } = params;
  const entries: ChatTimelineEntry[] = [];
  let currentRun: ChatMessageRun | undefined;
  let previous: ChatTimelineMessage | undefined;

  for (const message of messages) {
    const createdAt = dayjs(message.createdAt);
    const startsDay = !previous || !createdAt.isSame(previous.createdAt, 'day');
    const startsUnread = message.messageId === firstUnreadMessageId;

    if (startsDay) {
      entries.push({
        date: message.createdAt,
        key: `day-${createdAt.format('YYYY-MM-DD')}`,
        type: 'day',
      });
    }
    if (startsUnread) {
      entries.push({ key: 'unread', type: 'unread' });
    }

    const continuesRun =
      currentRun !== undefined &&
      previous !== undefined &&
      !startsDay &&
      !startsUnread &&
      previous.sender.userId === message.sender.userId &&
      createdAt.diff(previous.createdAt, 'minute') < RUN_GAP_MINUTES;

    if (continuesRun && currentRun) {
      currentRun.messages.push(message);
    } else {
      currentRun = {
        isOwn: message.sender.userId === currentUserId,
        messages: [message],
        sender: message.sender,
      };
      entries.push({ key: `run-${message.messageId}`, run: currentRun, type: 'run' });
    }
    previous = message;
  }

  return entries;
};

export const findFirstUnreadMessageId = (
  messages: ChatMessageDto[],
  currentUserId: string,
): number | undefined =>
  messages.find(message => !message.isRead && message.sender.userId !== currentUserId)?.messageId;

export const chatDayLabel = (date: string, now: Dayjs = dayjs()): string => {
  const value = dayjs(date);
  if (value.isSame(now, 'day')) {
    return capitalizeFirstLetter(i18next.t('components.listDetails.date.today'));
  }
  if (value.isSame(now.subtract(1, 'day'), 'day')) {
    return capitalizeFirstLetter(i18next.t('components.listDetails.date.yesterday'));
  }
  if (now.startOf('day').diff(value.startOf('day'), 'day') < 7) {
    return capitalizeFirstLetter(value.format('dddd, D MMMM'));
  }
  return value.format(value.isSame(now, 'year') ? 'D MMMM' : 'D MMMM YYYY');
};
